import axios from 'axios';

// Use environment variable if available, otherwise use relative path for production
const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if user was authenticated (had a token)
      // This prevents redirect when login fails
      const hadToken = localStorage.getItem('token');
      localStorage.removeItem('token');

      if (hadToken && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) =>
    api.post('/auth/login', { email, password }),
  register: (username, email, password, preferredLanguage = 'pt-BR') =>
    api.post('/auth/register', { username, email, password, preferred_language: preferredLanguage }),
  verifyEmail: (email, code) =>
    api.post('/auth/verify-email', { email, code }),
  resendVerificationCode: (email) =>
    api.post('/auth/resend-verification-code', { email }),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
  forgotPassword: (email) => api.post('/settings/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/settings/reset-password', { token, new_password: newPassword }),
};

// Users API (admin)
export const usersAPI = {
  getAll: () => api.get('/users/'),
  create: (data) => api.post('/users/', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getStats: (id) => api.get(`/users/${id}/stats`),
};

// TMDB API
export const tmdbAPI = {
  search: (query, page = 1) =>
    api.get('/tmdb/search', { params: { query, page } }),
  getTrending: (mediaType = 'all', timeWindow = 'week', page = 1) =>
    api.get('/tmdb/trending', { params: { media_type: mediaType, time_window: timeWindow, page } }),
  getMovieDetails: (id) => api.get(`/tmdb/movie/${id}`),
  getTVDetails: (id) => api.get(`/tmdb/tv/${id}`),
  getSeasonDetails: (tvId, seasonNumber) =>
    api.get(`/tmdb/tv/${tvId}/season/${seasonNumber}`),
  discoverMovies: (page = 1) =>
    api.get('/tmdb/discover/movie', { params: { page } }),
  discoverTV: (page = 1) =>
    api.get('/tmdb/discover/tv', { params: { page } }),
  getMovieCredits: (id) => api.get(`/tmdb/movie/${id}/credits`),
  getTVCredits: (id) => api.get(`/tmdb/tv/${id}/credits`),
};

// Watchlist API
export const watchlistAPI = {
  getAll: () => api.get('/watchlist/'),
  getMovies: () => api.get('/watchlist/movies'),
  getTV: () => api.get('/watchlist/tv'),
  getItem: (id) => api.get(`/watchlist/${id}`),
  add: (data) => api.post('/watchlist/', data),
  update: (id, data) => api.put(`/watchlist/${id}`, data),
  remove: (id) => api.delete(`/watchlist/${id}`),
  check: (tmdbId, mediaType) =>
    api.get(`/watchlist/check/${tmdbId}/${mediaType}`),
  syncLanguage: () => api.post('/watchlist/sync-language'),
  getTop10Monthly: () => api.get('/watchlist/top10/monthly'),
  syncUserTVShows: (callbacks) => {
    const token = localStorage.getItem('token');

    if (!token) {
      console.error('No token found in localStorage');
      callbacks.onError?.({ message: 'Not authenticated. Please login again.' });
      return {
        close: () => {},
      };
    }

    // Pass token as query parameter (EventSource doesn't support custom headers)
    // Use API_URL to support both development and production environments
    const url = `${API_URL}/settings/sync-user-tv-shows-stream?token=${encodeURIComponent(token)}`;

    console.log('Connecting to User TV Sync SSE:', url);

    const eventSource = new EventSource(url);

    // Set up event listeners
    eventSource.onmessage = (event) => {
      try {
        console.log('SSE message received:', event.data);
        const data = JSON.parse(event.data);

        if (data.type === 'progress') {
          callbacks.onProgress?.(data);
        } else if (data.type === 'complete') {
          callbacks.onComplete?.(data);
          eventSource.close();
        } else if (data.type === 'error') {
          callbacks.onError?.(data);
          eventSource.close();
        }
      } catch (error) {
        console.error('Error parsing SSE data:', error);
        callbacks.onError?.({ message: 'Error parsing progress data', error: error.message });
        eventSource.close();
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      console.error('EventSource readyState:', eventSource.readyState);
      callbacks.onError?.({ message: 'Connection error. Please check if backend is running.' });
      eventSource.close();
    };

    return {
      close: () => {
        console.log('Closing User TV Sync SSE connection');
        eventSource.close();
      },
    };
  },
};

// Settings API (admin)
export const settingsAPI = {
  getTMDBKeyStatus: () => api.get('/settings/tmdb-key'),
  updateTMDBKey: (key) => api.put('/settings/tmdb-key', { tmdb_api_key: key }),
  getStats: () => api.get('/settings/stats'),
  getDashboardStats: (params) => api.get('/settings/dashboard-stats', { params }),
  getTop10Stats: (params) => api.get('/settings/top10-stats', { params }),
  syncTVShows: () => api.post('/settings/sync-tv-shows'),
  getSMTPStatus: () => api.get('/settings/smtp'),
  updateSMTP: (data) => api.put('/settings/smtp', data),
  testSMTPConnection: () => api.get('/settings/smtp/test-connection'),
  testSMTPEmail: (email) => api.post('/settings/test-email', null, { params: { test_email: email } }),
  getSystemVersion: () => api.get('/settings/system-version'),
  updateSystemVersion: (version) => api.put('/settings/system-version', { version: version }),
  getRegistrationStatus: () => api.get('/settings/registration-enabled'),
  updateRegistrationStatus: (enabled) => api.put('/settings/registration-enabled', { enabled }),
  syncTVShowsStream: (callbacks) => {
    const token = localStorage.getItem('token');

    if (!token) {
      console.error('No token found in localStorage');
      callbacks.onError?.({ message: 'Not authenticated. Please login again.' });
      return {
        close: () => {},
      };
    }

    // Pass token as query parameter (EventSource doesn't support custom headers)
    // Use API_URL to support both development and production environments
    const url = `${API_URL}/settings/sync-tv-shows-stream?token=${encodeURIComponent(token)}`;

    console.log('Connecting to SSE:', url);

    const eventSource = new EventSource(url);

    // Set up event listeners
    eventSource.onmessage = (event) => {
      try {
        console.log('SSE message received:', event.data);
        const data = JSON.parse(event.data);

        if (data.type === 'progress') {
          callbacks.onProgress?.(data);
        } else if (data.type === 'complete') {
          callbacks.onComplete?.(data);
          eventSource.close();
        } else if (data.type === 'error') {
          callbacks.onError?.(data);
          eventSource.close();
        }
      } catch (error) {
        console.error('Error parsing SSE data:', error);
        callbacks.onError?.({ message: 'Error parsing progress data', error: error.message });
        eventSource.close();
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      console.error('EventSource readyState:', eventSource.readyState);
      callbacks.onError?.({ message: 'Connection error. Please check if backend is running.' });
      eventSource.close();
    };

    return {
      close: () => {
        console.log('Closing SSE connection');
        eventSource.close();
      },
    };
  },
};

// System Version API (no auth required for login screen)
export const systemAPI = {
  getVersion: () => api.get('/settings/system-version'),
};

export default api;
